// frontend/src/pages/PricingPage/PricingPage.jsx

import React, { useCallback, useState, useEffect, useContext, useRef } from 'react';
import { motion } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper, Button, Divider, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import PsychologyIcon from '@mui/icons-material/Psychology';
import QrCodeIcon from '@mui/icons-material/QrCode';
import SecurityIcon from '@mui/icons-material/Security';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ApiIcon from '@mui/icons-material/Api';
import { Helmet } from 'react-helmet-async';
import PricingCalculator from './PricingCalculator';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';
import './PricingPage.css';

const gridContainerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
};

const gridItemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1 },
};

// --- Reusable Pricing Row Component ---
const PricingRow = ({ label, value, isHighlight, stacked }) => (
    <Box className={`pricing-row ${isHighlight ? 'highlight' : ''} ${stacked ? 'stacked' : ''}`}>
        <Typography variant="body2" className="row-label">{label}</Typography>
        <Typography variant="body2" className="row-value">{value}</Typography>
    </Box>
);

const PricingPage = () => {
    const { t } = useTranslation();
    const { user, isAuthenticated } = useContext(AuthContext);
    const { openDocViewer } = useContext(UIContext);
    const [pricingConfig, setPricingConfig] = useState(null);
    const [, setLoading] = useState(true);
    const [isScrolled, setIsScrolled] = useState(false);
    const containerRef = useRef(null);

    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const particlesInit = useCallback(async (engine) => { await loadSlim(engine); }, []);
    const particlesConfig = {
        background: { color: { value: 'transparent' } },
        fpsLimit: 60,
        interactivity: { events: { onHover: { enable: true, mode: 'repulse' }, resize: true } },
        particles: {
            color: { value: '#ffffff' },
            links: { color: '#4fc3f7', distance: 150, enable: true, opacity: 0.2, width: 1 },
            move: { direction: 'none', enable: true, outModes: { default: 'bounce' }, random: false, speed: 0.5 },
            number: { density: { enable: true, area: 800 }, value: 60 },
            opacity: { value: 0.3 },
            size: { value: { min: 1, max: 3 } },
        },
        detectRetina: true,
    };

    useEffect(() => {
        fetch('/api/config/billing')
            .then(res => res.json())
            .then(data => {
                setPricingConfig(data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Failed to load pricing config", err);
                setLoading(false);
            });
    }, []);

    // Format helper: Units -> USD string
    const formatCost = (units) => {
        // ROBUST CHECK: Ensure config AND the specific meta field exist
        if (!pricingConfig || !pricingConfig?.billing_meta?.unit_to_currency_ratio) {
            return "..."; 
        }
        
        const ratio = pricingConfig.billing_meta.unit_to_currency_ratio;
        const cost = (units * ratio).toFixed(3);
        
        // Handle cases where units might be undefined (passed from a missing config key)
        const safeUnits = units || 0;
        
        return t('pricingPage.unitsCostFormat', { units: safeUnits, cost: parseFloat(cost) });
    };

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`pp-page-container ${isScrolled ? 'scrolled' : ''}`}
        >
            <Particles id="tsparticles-pricing" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('pricingPage.helmet.title')}</title>
                <meta name="description" content={t('pricingPage.helmet.description')} />
            </Helmet>
            
            <div className="pp-nav-container">
                <UniversalNav />
            </div>

            <main className="pp-content-wrapper">
                <Box className="pp-glass-container">
                    
                    {/* --- HEADER SECTION --- */}
                    <Box className="pp-header">
                        <Chip 
                            label={t('pricingPage.unitBanner')} 
                            className="pp-header-chip"
                        />
                        <Typography variant="h3" component="h1">
                            {t('pricingPage.mainTitle')}
                        </Typography>
                        <Typography variant="subtitle1" component="p" sx={{ mb: 2 }}>
                            {t('pricingPage.subtitle')}
                        </Typography>
                        
                        {!user && (
                            <Button 
                                variant="text" 
                                startIcon={<HelpOutlineIcon />}
                                onClick={() => openDocViewer('business/billing_and_usage_units.md')}
                                className="pp-doc-link"
                            >
                                {t('pricingPage.docLink')}
                            </Button>
                        )}
                    </Box>

                    {/* --- GRID SECTION --- */}
                    <motion.div
                        className="pricing-grid"
                        variants={gridContainerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        {/* 1. CORE ANALYSIS CARD */}
                        <motion.div variants={gridItemVariants}>
                            <Paper elevation={2} className="pricing-card analysis-card">
                                <Box className="card-icon-header centered-icon">
                                    <PsychologyIcon />
                                </Box>
                                <Typography variant="caption" className="card-usage-type">{t('pricingPage.sections.analysis.usageType')}</Typography>
                                <Typography variant="h6" component="h2" className="card-title">{t('pricingPage.sections.analysis.title')}</Typography>
                                <Typography variant="body2" className="card-subtitle">{t('pricingPage.sections.analysis.subtitle')}</Typography>
                                <Divider className="card-divider" />

                                <Typography variant="caption" className="card-description">
                                    {t('pricingPage.sections.analysis.description')}
                                </Typography>

                                <Typography variant="caption" className="task-note-top">
                                    {t('pricingPage.sections.analysis.items.addonsNote')}
                                </Typography>

                                <PricingRow label={t('pricingPage.sections.analysis.items.text')} value={formatCost(pricingConfig?.prices?.analysis_new?.text)} />
                                <PricingRow label={t('pricingPage.sections.analysis.items.image')} value={formatCost(pricingConfig?.prices?.analysis_new?.image)} />
                                <PricingRow label={t('pricingPage.sections.analysis.items.audio')} value={formatCost(pricingConfig?.prices?.analysis_new?.audio)} />
                                <PricingRow label={t('pricingPage.sections.analysis.items.video')} value={formatCost(pricingConfig?.prices?.analysis_new?.video)} />
                                
                                <Divider className="card-divider-light" />
                                
                                <Box className="highlight-box">
                                    <PricingRow 
                                        isHighlight 
                                        label={t('pricingPage.sections.analysis.items.addons')}
                                        value={`+${formatCost(pricingConfig?.prices?.analysis_addons?.explanation)}${t('pricingPage.sections.analysis.items.addonsCostSuffix')}`} 
                                    />
                                </Box>
                            </Paper>
                        </motion.div>

                        {/* 2. LICENSING CARD */}
                        <motion.div variants={gridItemVariants}>
                            <Paper elevation={2} className="pricing-card activation-card">
                                <Box className="card-icon-header featured-icon centered-icon">
                                    <QrCodeIcon />
                                </Box>
                                <Typography variant="caption" className="card-usage-type">{t('pricingPage.sections.licensing.usageType')}</Typography>
                                <Typography variant="h6" component="h2" className="card-title">{t('pricingPage.sections.licensing.title')}</Typography>
                                <Typography variant="body2" className="card-subtitle">{t('pricingPage.sections.licensing.subtitle')}</Typography>
                                <Divider className="card-divider" />

                                <Typography variant="caption" className="card-description">
                                    {t('pricingPage.sections.licensing.description')}
                                </Typography>

                                <PricingRow stacked label={t('pricingPage.sections.licensing.items.activation')} value={formatCost(pricingConfig?.prices?.content_license?.image_base_fee)} />
                                <PricingRow stacked label={t('pricingPage.sections.licensing.items.daily')} value={`${formatCost(pricingConfig?.prices?.content_license?.image_daily_fee)}${t('pricingPage.sections.licensing.items.unitDay')}`} />
                                <PricingRow stacked label={t('pricingPage.sections.licensing.items.scan')} value={`${formatCost(pricingConfig?.prices?.verification_scan_fee?.base_cost)}${t('pricingPage.sections.licensing.items.unitScan')}`} />
                                
                            </Paper>
                        </motion.div>

                        {/* 3. AD TECH CARD */}
                        <motion.div variants={gridItemVariants}>
                            <Paper elevation={2} className="pricing-card activation-card">
                                <Box className="card-icon-header featured-icon centered-icon">
                                    <SecurityIcon />
                                </Box>
                                <Typography variant="caption" className="card-usage-type">{t('pricingPage.sections.adtech.usageType')}</Typography>
                                <Typography variant="h6" component="h2" className="card-title">{t('pricingPage.sections.adtech.title')}</Typography>
                                <Typography variant="body2" className="card-subtitle">{t('pricingPage.sections.adtech.subtitle')}</Typography>
                                <Divider className="card-divider" />

                                <Typography variant="caption" className="card-description">
                                    {t('pricingPage.sections.adtech.description')}
                                </Typography>

                                <PricingRow stacked label={t('pricingPage.sections.adtech.items.cpm')} value={`${t('pricingPage.sections.adtech.items.from')}${formatCost(pricingConfig?.prices?.impression_cpm?.image)}${t('pricingPage.sections.adtech.items.unitCpm')}`} />
                                <PricingRow stacked label={t('pricingPage.sections.adtech.items.cpc')} value={`${formatCost(pricingConfig?.prices?.verification_access?.base_cost)}${t('pricingPage.sections.adtech.items.unitClick')}`} />
                            </Paper>
                        </motion.div>
                    </motion.div>

                    {/* --- 4. NEW API BANNER SECTION --- */}
                    <motion.div 
                        variants={gridItemVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, amount: 0.2 }}
                    >
                        <Paper elevation={2} className="pricing-card api-banner-card">
                            <Box className="api-banner-content">
                                <Box className="card-icon-header" sx={{ mb: 0, mr: 3, flexShrink: 0 }}>
                                    <ApiIcon />
                                </Box>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="h6" component="h2" className="card-title" sx={{ mb: 0 }}>
                                        {t('pricingPage.sections.api.title')}
                                    </Typography>
                                    <Typography variant="subtitle2" component="p" sx={{ color: 'var(--secondary-color)', mb: 1, fontWeight: 'bold' }}>
                                        {t('pricingPage.sections.api.subtitle')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                        {t('pricingPage.sections.api.description')}
                                    </Typography>
                                </Box>
                                {isAuthenticated && (
                                    <Box className="api-banner-action">
                                        <Button 
                                            variant="outlined" 
                                            onClick={(e) => {
                                                e.stopPropagation(); 
                                                openDocViewer('api/api_reference.md');
                                            }}
                                            sx={{ 
                                                borderColor: 'var(--secondary-color)', 
                                                color: 'var(--secondary-color)',
                                                whiteSpace: 'nowrap',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(79, 195, 247, 0.1)',
                                                    borderColor: 'var(--btn-hover-color)',
                                                }
                                            }}
                                        >
                                            {t('pricingPage.sections.api.button')}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </motion.div>

                    <PricingCalculator pricingConfig={pricingConfig} />

                    <Box className="pp-footer-cta">
                        <Typography variant="h4" component="h2" className="cta-title">{t('pricingPage.cta.title')}</Typography>
                        <Box className="cta-button-container">
                            <Button 
                                variant="contained" 
                                component={RouterLink} 
                                to="/login"
                                size="large"
                                className="cta-button-main"
                            >
                                {t('pricingPage.cta.button')}
                            </Button>
                        </Box>
                    </Box>

                </Box>
            </main>
        </div>
    );
};

export default PricingPage;